import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION = "posts";

export function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
}

// ─────────────────────────────────────────────────────────────────────────────
// getTimestamp
//
// Resolves a post's effective date for sorting purposes.
// Priority order:
//   1. publishDate  — the date deliberately set in the admin editor
//   2. createdAt    — Firestore server timestamp set on document creation
//   3. 0            — fallback so posts with no date sort to the bottom
//
// Both Firestore Timestamps ({ seconds, nanoseconds }) and plain JS Date
// objects (stored after new Date(form.publishDate) in the editor) are handled.
// ─────────────────────────────────────────────────────────────────────────────
function getTimestamp(post) {
  const pd = post.publishDate;
  const ca = post.createdAt;

  // Extracts unix seconds from a Firestore Timestamp or a JS Date
  const toSeconds = (val) => {
    if (!val) return null;
    if (typeof val.seconds === "number") return val.seconds;           // Firestore Timestamp
    if (val?.toDate) return Math.floor(val.toDate().getTime() / 1000); // Firestore Timestamp (toDate method)
    if (val instanceof Date) return Math.floor(val.getTime() / 1000);  // Plain JS Date
    return null;
  };

  return toSeconds(pd) ?? toSeconds(ca) ?? 0;
}

export async function createPost(data) {
  const slug = data.slug || slugify(data.title);
  return await addDoc(collection(db, COLLECTION), {
    ...data,
    slug,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updatePost(id, data) {
  const ref = doc(db, COLLECTION, id);
  return await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePost(id) {
  const ref = doc(db, COLLECTION, id);
  return await deleteDoc(ref);
}

export async function getPost(id) {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getPostBySlug(slug) {
  const q = query(
    collection(db, COLLECTION),
    where("slug", "==", slug),
    where("status", "==", "published")
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

export async function getAllPosts(filters = {}) {
  let constraints = [];
  if (filters.status) {
    constraints.push(where("status", "==", filters.status));
  }
  if (filters.category) {
    constraints.push(where("category", "==", filters.category));
  }
  if (filters.featured) {
    constraints.push(where("featured", "==", true));
  }

  const q = query(collection(db, COLLECTION), ...constraints);
  const snap = await getDocs(q);
  let docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // ── Sort newest → oldest by effective date ────────────────────────────────
  // publishDate takes priority over createdAt. Sorting in memory avoids the
  // need for composite Firestore indexes on every filter combination.
  docs.sort((a, b) => getTimestamp(b) - getTimestamp(a));

  if (filters.limit) {
    docs = docs.slice(0, filters.limit);
  }

  return docs;
}

export async function getPublishedPosts(filters = {}) {
  return getAllPosts({ ...filters, status: "published" });
}

export async function getFeaturedPosts(limitCount = 3) {
  return getAllPosts({ status: "published", featured: true, limit: limitCount });
}

export async function subscribeNewsletter(email) {
  if (!email || !email.includes("@")) throw new Error("Invalid email");
  return await addDoc(collection(db, "newsletter"), {
    email: email.trim().toLowerCase(),
    subscribedAt: serverTimestamp(),
  });
}
